import { useState } from 'react'
import { SEO } from '../components/SEO'
import { SEOService, defaultFaq } from '../services/seoService'
import { Link } from 'react-router-dom'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Badge } from '../components/ui/badge'
import { TextAnimate } from '../components/ui/text-animate'
import { BentoGrid, BentoCard } from '../components/ui/bento-grid'
import { PricingPlans } from '../components/pricing/PricingPlans'
import { FOUNDING_PARTNER, LOWEST_PRICE_LABEL } from '../data/pricing'
import {
  Shield,
  CheckCircle,
  ClipboardList,
  BarChart3,
  FileDown,
  Users,
  ArrowRight,
  AlertTriangle,
  HelpCircle,
  Menu,
  X,
  Sparkles,
} from 'lucide-react'

export function LandingPage() {
  const seo = SEOService.generatePageSEO('consultant')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  // Combine Software + Organization + FAQ into a single @graph for richer Google snippets
  const combinedJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      SEOService.generateStructuredData('software'),
      SEOService.generateStructuredData('organization'),
      SEOService.generateStructuredData('faq', { faq: defaultFaq }),
    ],
  }

  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonical={SEOService.getCanonical('/')}
        ogImage={SEOService.getOgImage()}
        structuredData={combinedJsonLd}
      />

      <div className="min-h-screen bg-white">
        {/* Free Assessment Banner — Top of Page */}
        <div className="bg-green-600 px-4 py-3 text-center" role="banner">
          <Link to="/register" className="text-white font-bold hover:underline inline-flex items-center focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-green-600 rounded-sm" aria-label="Run free CMMC gap assessment — see your compliance score in 15 minutes, no credit card required">
            <CheckCircle className="h-5 w-5 mr-2" aria-hidden="true" />
            FREE CMMC Gap Assessment — See your compliance score in 15 minutes. No credit card required.
            <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
        {/* Navigation */}
        <nav className="bg-white border-b" aria-label="Main navigation">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Shield className="h-8 w-8 text-blue-600" aria-hidden="true" />
              <span className="text-xl font-bold text-foreground">Level2Logic</span>
            </div>
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4">
              <Link to="/cmmc-for-consultants" className="text-muted-foreground hover:text-foreground font-medium focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded px-2 py-1" aria-label="CMMC software for consultants">
                For Consultants
              </Link>
              <Link to="/features" className="text-muted-foreground hover:text-foreground font-medium focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded px-2 py-1" aria-label="Platform features">
                Features
              </Link>
              <Link to="/blog" className="text-muted-foreground hover:text-foreground font-medium focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded px-2 py-1" aria-label="Read our CMMC blog">
                Blog
              </Link>
              <Link to="/resources" className="text-muted-foreground hover:text-foreground font-medium focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded px-2 py-1" aria-label="Browse free CMMC resources">
                Free Resources
              </Link>
              <Link to="/login" className="text-muted-foreground hover:text-foreground font-medium focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded px-2 py-1" aria-label="Sign in to your account">
                Sign In
              </Link>
              <Link
                to="/register"
                aria-label="Get started with CMMC Readiness"
              >
                <Button size="default" className="bg-blue-600 hover:bg-blue-700">
                  Get Started
                </Button>
              </Link>
            </div>
            {/* Mobile Hamburger Menu Button */}
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center p-3 rounded-md text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
              aria-label={mobileMenuOpen ? 'Close main menu' : 'Open main menu'}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
          {/* Mobile Menu Panel */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-border bg-white" id="mobile-menu">
              <div className="px-4 py-3 space-y-2">
                <Link to="/features" className="block px-4 py-3 text-muted-foreground hover:bg-muted rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-ring" onClick={() => setMobileMenuOpen(false)} aria-label="Platform features">
                  Features
                </Link>
                <Link to="/blog" className="block px-4 py-3 text-muted-foreground hover:bg-muted rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-ring" onClick={() => setMobileMenuOpen(false)} aria-label="Read our CMMC blog">
                  Blog
                </Link>
                <Link to="/resources" className="block px-4 py-3 text-muted-foreground hover:bg-muted rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-ring" onClick={() => setMobileMenuOpen(false)} aria-label="Browse free CMMC resources">
                  Free Resources
                </Link>
                <Link to="/login" className="block px-4 py-3 text-muted-foreground hover:bg-muted rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-ring" onClick={() => setMobileMenuOpen(false)} aria-label="Sign in to your account">
                  Sign In
                </Link>
                <Link to="/register" className="block px-4 py-3 bg-blue-600 text-white rounded-lg font-medium text-center hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-ring" onClick={() => setMobileMenuOpen(false)} aria-label="Get started with CMMC Readiness">
                  Get Started
                </Link>
              </div>
            </div>
          )}
        </nav>

        {/* Hero Section — honest, clear value prop */}
        <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 relative z-10">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="max-w-3xl lg:w-1/2">
                <Badge variant="secondary" className="inline-flex items-center bg-green-500/20 border border-green-400/30 rounded-full px-4 py-2 mb-6 text-green-300 hover:bg-green-500/20" role="status">
                  <CheckCircle className="h-4 w-4 mr-2" aria-hidden="true" />
                  <span className="text-sm font-medium">Free CMMC Gap Assessment — No credit card required</span>
                </Badge>
                <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                  <TextAnimate
                    animation="blurInUp"
                    by="word"
                    as="span"
                    className="text-white"
                  >
                    CMMC Compliance Software for Consultants
                  </TextAnimate>
                  <span className="block text-blue-300 mt-2">Manage 20 clients with the effort it takes to manage 3.</span>
                </h1>
                <p className="text-lg md:text-xl mb-8 text-slate-300 leading-relaxed">
                  The multi-tenant CMMC Level 2 platform for Registered Practitioners.
                  Walk every client through 110 practices, auto-generate SSP and POA&M,
                  and track readiness across your entire portfolio from one dashboard.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/register"
                    aria-label="Run free CMMC gap assessment"
                  >
                    <Button size="lg" className="bg-green-500 text-white hover:bg-green-400 shadow-lg shadow-green-500/30">
                      Run Free Gap Assessment
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                  <Link
                    to="/login"
                    aria-label="Sign in to your existing account"
                  >
                    <Button size="lg" variant="outline" className="border-slate-500 text-slate-300 hover:bg-slate-800 hover:text-white bg-transparent">
                      Sign In
                    </Button>
                  </Link>
                </div>
                <p className="mt-4 text-sm text-slate-400">
                  {FOUNDING_PARTNER.summary} Plans from {LOWEST_PRICE_LABEL} after that.
                </p>
                <p className="mt-2 text-sm text-slate-400">
                  Self-assessment readiness for CMMC compliance. Not a substitute for a C3PAO assessment.
                </p>
              </div>
              <div className="lg:w-1/2 w-full mt-10 lg:mt-0 rounded-xl border border-slate-700 shadow-[0_0_50px_rgba(59,130,246,0.3)] overflow-hidden transform hover:scale-[1.02] transition-transform duration-500">
                <img src="/cmmc_dashboard_mockup.svg" alt="CMMC Compliance Dashboard Mockup showing compliance score overview and domain breakdown" className="w-full h-auto object-cover" loading="lazy" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }} />
              </div>
            </div>
          </div>
        </section>

        {/* Quick Quiz — Top 5 Questions */}
        <section className="py-16 bg-white border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-8">
              Managing CMMC Assessments for Multiple Clients?
            </h2>
            <p className="text-center text-muted-foreground mb-8 text-lg">If you are a CMMC consultant or Registered Practitioner, answer these 5 questions:</p>
            <div className="space-y-4">
              {[
                { q: "1. Do you manage CMMC assessments for defense contractors?", note: "If yes → You need a tool to manage multiple clients efficiently" },
                { q: "2. Are you tracking assessments across spreadsheets or email?", note: "If yes → There is a better way — automate your workflow" },
                { q: "3. Do you need to generate SPRS scores and SSPs for clients?", note: "If yes → Auto-generate reports instead of manual calculations" },
                { q: "4. Are your clients asking for audit-ready documentation?", note: "If yes → Generate PDF reports, SSPs, and POA&Ms instantly" },
                { q: "5. Are you spending more than 20 hours per client on compliance?", note: "If yes → Our platform cuts that time in half — try the free assessment" },
              ].map((item, idx) => (
                <Card key={idx} className="border-gray-200 hover:border-blue-300 transition-colors">
                  <CardContent className="p-5">
                    <p className="text-foreground font-semibold text-lg">{item.q}</p>
                    <p className="text-muted-foreground mt-1">{item.note}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/register" aria-label="See how you score with free assessment">
                <Button size="lg" className="bg-green-500 text-white hover:bg-green-400 shadow-lg shadow-green-500/30">
                  See How You Score — Free Assessment
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <p className="mt-3 text-sm text-muted-foreground">Takes 15 minutes. No credit card required.</p>
            </div>
          </div>
        </section>

        {/* Who This Is For */}
        <section className="py-16 bg-slate-50 border-b border-gray-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-4">
              Built for CMMC Consultants and Registered Practitioners
            </h2>
            <p className="text-center text-muted-foreground mb-10 text-lg max-w-2xl mx-auto">
              If you guide defense contractors through CMMC Level 2, this replaces the assessment spreadsheet, the Word templates and the evidence folder with one workspace per client.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-gray-200 shadow-sm">
                <CardHeader>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <Users className="h-6 w-6 text-blue-600" aria-hidden="true" />
                  </div>
                  <CardTitle>CMMC Registered Practitioners</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">Manage assessments for 5-20 clients from one dashboard. No more spreadsheets.</CardDescription>
                </CardContent>
              </Card>
              <Card className="border-gray-200 shadow-sm">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                    <Shield className="h-6 w-6 text-green-600" aria-hidden="true" />
                  </div>
                  <CardTitle>Cybersecurity Consultants</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">Add CMMC readiness to your service offerings without hiring specialists.</CardDescription>
                </CardContent>
              </Card>
              <Card className="border-gray-200 shadow-sm">
                <CardHeader>
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                    <ClipboardList className="h-6 w-6 text-amber-600" aria-hidden="true" />
                  </div>
                  <CardTitle>Small Defense Contractors</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">DIY your gap assessment before spending $15K+ on a consultant.</CardDescription>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 bg-white border-b border-gray-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-10">
              How It Works
            </h2>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { step: '1', title: 'Add Your Clients', desc: 'Create a workspace for each defense contractor you work with.' },
                { step: '2', title: 'Walk Through 110 Practices', desc: 'Assess each CMMC Level 2 practice with plain-English guidance.' },
                { step: '3', title: 'See Your Gaps', desc: 'Get a compliance score and prioritized gap list by domain.' },
                { step: '4', title: 'Generate Reports', desc: 'Export SSPs, POA&Ms, and SPRS scores as PDF for your clients.' },
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="w-14 h-14 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">{item.step}</div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* The Problem — why this exists */}
        <section className="py-16 sm:py-20 bg-amber-50 border-y border-amber-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
              <div className="md:w-1/2">
                <div className="rounded-xl shadow-xl border border-amber-200 bg-slate-800 p-8 text-center"><Shield className="h-16 w-16 text-amber-400 mx-auto mb-4" /><p className="text-amber-200 text-lg font-semibold">Defense Manufacturing Operations</p><p className="text-amber-100/60 text-sm mt-2">CMMC compliance is required for all DoD contracts</p></div>
              </div>
              <div className="md:w-1/2 text-left">
                <AlertTriangle className="h-10 w-10 text-amber-600 mb-4" aria-hidden="true" />
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                  Most small contractors don't know where they stand.
                </h2>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  CMMC Level 2 has 110 practices across 16 security domains. Consultants charge
                  $15,000–$50,000+ just for a gap assessment. Many small defense contractors can't
                  afford that — but they can't afford to lose their DoD contracts either.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  You need a way to figure out where you are <em className="font-semibold text-amber-900">before</em> you decide what help you need.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What the platform actually does — Bento Grid */}
        <section className="py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                What This Platform Actually Does
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                No buzzwords. Here's what you get.
              </p>
            </div>

            <BentoGrid className="max-w-5xl mx-auto">
              <BentoCard
                name="Guided Self-Assessment"
                description="Walk through each of the 110 Level 2 practices one by one. Each practice includes a plain-English description and implementation guidance so you know what 'compliant' actually looks like."
                Icon={ClipboardList}
                background={<div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-transparent opacity-60" />}
                className="lg:col-span-1"
              />
              <BentoCard
                name="Gap Identification"
                description="See exactly which practices you're failing or partially meeting. The platform calculates your compliance score by domain so you know which areas need the most work."
                Icon={AlertTriangle}
                background={<div className="absolute inset-0 bg-gradient-to-br from-red-50 to-transparent opacity-60" />}
                className="lg:col-span-1"
              />
              <BentoCard
                name="Compliance Scoring"
                description="Get a real-time compliance score as you assess each practice. See domain-by-domain breakdown so you can prioritize remediation where it matters most."
                Icon={BarChart3}
                background={<div className="absolute inset-0 bg-gradient-to-br from-green-50 to-transparent opacity-60" />}
                className="lg:col-span-1"
              />
              <BentoCard
                name="Export Your Results"
                description="Download your gap analysis as a CSV. Use it to brief your team, plan remediation, or share with a consultant or C3PAO assessor when you're ready for formal assessment."
                Icon={FileDown}
                background={<div className="absolute inset-0 bg-gradient-to-br from-purple-50 to-transparent opacity-60" />}
                className="lg:col-span-1"
              />
              <BentoCard
                name="Team Collaboration"
                description="Invite your IT staff, security team, or compliance officer. Different people can assess different domains based on their expertise. Role-based access keeps things organized."
                Icon={Users}
                background={<div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-transparent opacity-60" />}
                className="lg:col-span-1"
              />
              <BentoCard
                name="AI Compliance Advisor"
                description="Get guidance tailored to YOUR company from Claude, Anthropic's AI. Ask what a practice means for a 30-person machine shop. Upload a policy and get told if it covers the requirement."
                Icon={HelpCircle}
                background={
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-100 to-transparent opacity-60 flex items-center justify-center">
                    <Sparkles className="h-12 w-12 text-purple-300" aria-hidden="true" />
                  </div>
                }
                className="lg:col-span-1"
              />
            </BentoGrid>
          </div>
        </section>

        {/* What this is NOT — setting honest expectations */}
        <section className="py-16 bg-gray-50" aria-labelledby="expectations-heading">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
              <h2 id="expectations-heading" className="text-2xl font-bold text-foreground mb-6 text-center">
                Let's Be Clear About What This Is
              </h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <p className="text-gray-700">
                    <strong>This is a self-assessment tool.</strong> It helps you understand where you stand
                    against CMMC Level 2 requirements and what gaps you need to close.
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <p className="text-gray-700">
                    <strong>It saves you money on discovery.</strong> Instead of paying a consultant $15K+
                    just to tell you what's wrong, you can figure that out yourself and spend your budget on fixing things.
                  </p>
                </div>
                <div className="flex items-start space-x-3 bg-amber-50 p-4 rounded-lg border border-amber-200" role="alert">
                  <AlertTriangle className="h-6 w-6 text-amber-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <p className="text-gray-700">
                    <strong>This does not replace a C3PAO assessment.</strong> CMMC Level 2 certification
                    requires a formal assessment by an authorized C3PAO. This tool helps you prepare —
                    it doesn't certify you.
                  </p>
                </div>
                <div className="flex items-start space-x-3 bg-amber-50 p-4 rounded-lg border border-amber-200" role="alert">
                  <AlertTriangle className="h-6 w-6 text-amber-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <p className="text-gray-700">
                    <strong>Your results depend on your honesty.</strong> The assessment is only as good as
                    the answers you provide. Be truthful about your current state — that's the only way this helps.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section — honest about what each tier includes */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Simple Pricing
              </h2>
              <p className="text-lg text-muted-foreground">
                Priced on the clients you manage. Cancel anytime.
              </p>
            </div>

            <PricingPlans />

            <p className="text-center text-sm text-muted-foreground mt-8">
              Need something custom for a larger organization? <a href="mailto:contact@level2logic.com" className="text-blue-600 underline focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded">Contact us for custom pricing</a>.
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 sm:py-20 bg-slate-900 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-transparent to-slate-900" />
          </div>
          <div className="max-w-3xl mx-auto text-center px-4 sm:px-6 lg:px-8 relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Stop guessing. Start assessing.
            </h2>
            <p className="text-lg mb-8 text-slate-300">
              You can't fix what you can't measure. Run a self-assessment against all 110
              CMMC Level 2 practices and know exactly where your gaps are — in days, not months.
            </p>
            <Link to="/register" aria-label="Start your free CMMC assessment now">
              <Button size="lg" className="bg-green-500 text-white hover:bg-green-400 shadow-lg shadow-green-500/30">
                Start Your Free Assessment Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <p className="mt-4 text-sm text-slate-500">
              Plans from {LOWEST_PRICE_LABEL} after {FOUNDING_PARTNER.endsOnLabel}. No long-term commitment.
            </p>
          </div>
        </section>

        {/* Waitlist Section — Early Access */}
        <section className="py-16 bg-gradient-to-br from-blue-900 to-slate-900 text-white">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Get Early Access</h2>
            <p className="text-slate-300 mb-8 text-lg">Join the waitlist for early access to Level2Logic — the CMMC compliance platform built for consultants. Be the first to manage all your clients from one dashboard.</p>
            <Link to="/register" aria-label="Join the waitlist by registering">
              <Button size="lg" className="bg-green-500 text-white hover:bg-green-400">
                Join Waitlist
              </Button>
            </Link>
            <p className="mt-4 text-sm text-slate-400">No spam. Just early access when we launch.</p>
          </div>
        </section>

        {/* Blog Teaser */}
        <section className="py-16 bg-white border-t">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-2xl font-bold text-foreground mb-3">
                Technical CMMC Guides
              </h2>
              <p className="text-muted-foreground">
                Control-level detail for contractors and consultants. No fluff.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link to="/blog/nist-800-171-assessment-checklist" className="group border rounded-lg p-5 hover:border-blue-300 hover:shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2" aria-label="Read: What C3PAOs Actually Verify in Each of the 110 Practices">
                <p className="text-xs text-blue-600 font-medium mb-1">Assessment</p>
                <h3 className="text-sm font-semibold text-foreground group-hover:text-blue-600 transition-colors mb-2">
                  What C3PAOs Actually Verify in Each of the 110 Practices
                </h3>
                <p className="text-xs text-muted-foreground">14 min read</p>
              </Link>
              <Link to="/blog/sprs-score-breakdown" className="group border rounded-lg p-5 hover:border-blue-300 hover:shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2" aria-label="Read: How the -203 to 110 SPRS Scale Actually Works">
                <p className="text-xs text-green-600 font-medium mb-1">Scoring</p>
                <h3 className="text-sm font-semibold text-foreground group-hover:text-blue-600 transition-colors mb-2">
                  How the -203 to 110 SPRS Scale Actually Works
                </h3>
                <p className="text-xs text-muted-foreground">11 min read</p>
              </Link>
              <Link to="/blog/config-scanner-cmmc" className="group border rounded-lg p-5 hover:border-blue-300 hover:shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2" aria-label="Read: Automated Config Scanning for CM.L2-3.4.1">
                <p className="text-xs text-amber-600 font-medium mb-1">Automation</p>
                <h3 className="text-sm font-semibold text-foreground group-hover:text-blue-600 transition-colors mb-2">
                  Automated Config Scanning for CM.L2-3.4.1
                </h3>
                <p className="text-xs text-muted-foreground">9 min read</p>
              </Link>
            </div>
            <div className="text-center mt-8">
              <Link to="/blog" className="text-sm font-medium text-blue-600 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded px-2 py-1" aria-label="View all blog posts">
                View all posts →
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 bg-white border-t" role="contentinfo">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-muted-foreground">
            <div className="flex justify-center space-x-4 mb-3">
              <Link to="/tos" className="hover:text-foreground transition-colors">Terms</Link>
              <span className="text-gray-300">|</span>
              <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            </div>
            <p>
              This platform is a self-assessment tool. It does not provide CMMC certification.
              CMMC Level 2 certification requires a formal assessment by an authorized C3PAO.
            </p>
          </div>
        </footer>
      </div>
    </>
  )
}
